import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BorderBeam } from "antd";
import DigiTwinLogo from "../DigiTwin/components/DigiTwinLogo.js";
import "./Welcome.css";

function AnnouncementIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0" />
    </svg>
  );
}

function LayersIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 2 7 12 12 22 7 12 2" />
      <polyline points="2 17 12 22 22 17" />
      <polyline points="2 12 12 17 22 12" />
    </svg>
  );
}

function TeamIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 00-3-3.87" />
      <path d="M16 3.13a4 4 0 010 7.75" />
    </svg>
  );
}

// A single compact line (most recent announcement only), not a boxed panel — a landing
// screen shouldn't compete for attention with its own flagship CTA below it. The API already
// returns newest-first, so announcements[0] is the one to show; older ones (up to 5, per
// pages/announcements/announcements.md) aren't surfaced here — there's no archive view yet,
// which is fine at this content scale.
function AnnouncementLine() {
  const [announcement, setAnnouncement] = useState(null);
  const [fetchFailed, setFetchFailed] = useState(false);

  useEffect(() => {
    fetch("/api/announcements")
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => setAnnouncement((data.announcements || [])[0] || null))
      .catch((err) => {
        console.error("Failed to load announcements:", err);
        setFetchFailed(true);
      });
  }, []);

  if (!announcement && !fetchFailed) return null;

  return (
    <div className="announcement-line">
      <AnnouncementIcon size={14} />
      {fetchFailed ? (
        <span className="announcement-line-text">Couldn't load announcements right now.</span>
      ) : (
        <span className="announcement-line-text">
          <strong>{announcement.title}</strong>
          {announcement.date && <span className="announcement-line-date"> · {announcement.date}</span>}
        </span>
      )}
    </div>
  );
}

export default function Welcome() {
  return (
    <div className="welcome-page">

      <div className="welcome-hero">
        <img className="hero-logo" src="/proyapi-en.png" alt="PROYAPI" />
        <h1 className="hero-title">
          Digital <em>Infrastructure</em>
        </h1>
        <p className="hero-sub">
          From design to digital twins — integrated tools for infrastructure engineering and project delivery.
        </p>
      </div>

      <AnnouncementLine />

      {/* Flagship: the one module that's actually built and is the platform's main focus —
          a real featured entry point, not an equal-weight tile next to two empty placeholders.
          BorderBeam is reserved for this single card only — anywhere else it'd read as noise. */}
      <BorderBeam color="#4f9cf9">
        <Link to="digitwin" className="flagship-card">
          <div className="flagship-icon"><DigiTwinLogo size={40} /></div>
          <div className="flagship-body">
            <div className="flagship-name">DigiTwin</div>
            <div className="flagship-desc">
              Explore information models, alignments, cross sections, and quantity takeoffs.
            </div>
          </div>
          <span className="flagship-cta">Open DigiTwin →</span>
        </Link>
      </BorderBeam>

      {/* Everything else reachable is already in the top nav — this is a quiet secondary
          row, not another bank of cards. Learn/Softwares are left off entirely: both are
          still empty placeholders, and a landing screen advertising "coming soon" boxes
          reads as less finished, not more. */}
      <div className="quick-links-row">
        <Link to="bimframework" className="quick-link">
          <LayersIcon /> BIM Framework
        </Link>
        <Link to="team" className="quick-link">
          <TeamIcon /> Team
        </Link>
      </div>
    </div>
  );
}
